"use client";
import React, { useState } from 'react';
import dynamic from "next/dynamic";
import { useRouter } from 'next/navigation';
import { useAccount, useChainId, useWalletClient, useChains } from 'wagmi';
import { ethers } from 'ethers';
import { deployEventTicket } from '@/services/web3/eventTicketFactory';
import EventList from '@/components/lottery/EventList';

const WalletConnect = dynamic(
  () => import("@/components/wallet/WalletConnect").then(mod => mod.ClientWalletConnect),
  { ssr: false }
);

// Extend Window interface for global functions
declare global {
  interface Window {
    addEventToList?: (eventData: any) => void;
    refreshEventList?: () => void;
  }
}

const CreateEventPage = () => {
  const router = useRouter();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [totalTickets, setTotalTickets] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contractAddress, setContractAddress] = useState('');
  const [error, setError] = useState('');
  const chainId = useChainId();
  const chains = useChains();
  
  // Lấy thông tin network hiện tại
  const currentChain = chains.find(c => c.id === chainId);
  const nativeToken = currentChain?.nativeCurrency?.symbol || 'ETH';

  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setContractAddress('');
    if (!isConnected || !walletClient) {
      setError('Vui lòng kết nối ví trước khi tạo sự kiện.');
      return;
    }
    if (!name || !description || !price || !totalTickets) {
      setError('Vui lòng nhập đầy đủ thông tin.');
      return;
    }
    setIsSubmitting(true);
    try {
      const provider = new ethers.BrowserProvider(walletClient);
      const signer = await provider.getSigner();
      
      // Convert price from ETH to wei
      const txPrice = ethers.parseUnits(price, 18);
      const txMaxTickets = BigInt(totalTickets);
      
      console.log('Deployment parameters:', {
        name,
        description,
        price: price + ' ETH',
        priceInWei: txPrice.toString(),
        maxTickets: totalTickets
      });

      const contractAddr = await deployEventTicket({
        name,
        description,
        ticketPrice: txPrice.toString(),
        maxTickets: txMaxTickets.toString(),
        signer,
      });
      setContractAddress(contractAddr);
      
      // Lưu sự kiện vào danh sách
      if (window.addEventToList && address) {
        window.addEventToList({
          name,
          description,
          price,
          totalTickets,
          contractAddress: contractAddr,
          creator: address,
        });
        
        // Refresh danh sách sự kiện
        setTimeout(() => {
          if (window.refreshEventList) {
            window.refreshEventList();
          }
        }, 100);
      }
    } catch (err: unknown) {
      console.error('Deployment error:', err);
      if (err && typeof err === 'object' && 'message' in err) {
        setError((err as { message: string }).message);
      } else {
        setError('Đã có lỗi xảy ra khi deploy contract.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0a0a0a] text-gray-100 flex flex-col items-center justify-center">
      {/* Wallet Connect Button - Top Right Corner */}
      <div className="absolute top-4 right-4 z-50">
        <WalletConnect />
      </div>

      {/* Back Button - Top Left Corner */}
      <div className="absolute top-4 left-4 z-50">
        <button
          onClick={() => router.back()}
          className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Quay lại
        </button>
      </div>

      <section className="w-full flex flex-col items-center justify-center text-center py-24 px-4 relative">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="w-full h-full bg-gradient-to-br from-purple-900/40 via-indigo-900/30 to-black/80 blur-2xl"></div>
        </div>
        <h1 className="relative z-10 text-4xl md:text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 mb-6 drop-shadow-lg">
          Tạo sự kiện mới
        </h1>
        <p className="relative z-10 max-w-xl text-lg md:text-xl text-purple-200 mb-8">
          Điền thông tin để khởi tạo sự kiện NFT và deploy contract riêng cho sự kiện của bạn.
        </p>
        <div className="relative z-10 w-full max-w-xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-4 bg-white/10 p-8 rounded-2xl shadow-xl backdrop-blur border border-purple-800">
            <div>
              <label className="block font-medium mb-1 text-left">Tên sự kiện</label>
              <input
                type="text"
                className="w-full border border-purple-400 bg-transparent rounded px-3 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block font-medium mb-1 text-left">Mô tả</label>
              <textarea
                className="w-full border border-purple-400 bg-transparent rounded px-3 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={description}
                onChange={e => setDescription(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block font-medium mb-1 text-left">Giá vé ({nativeToken})</label>
              <input
                type="number"
                className="w-full border border-purple-400 bg-transparent rounded px-3 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={price}
                onChange={e => setPrice(e.target.value)}
                min="0"
                step="0.001"
                placeholder="0.01"
                required
              />
            </div>
            <div>
              <label className="block font-medium mb-1 text-left">Số lượng vé</label>
              <input
                type="number"
                className="w-full border border-purple-400 bg-transparent rounded px-3 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={totalTickets}
                onChange={e => setTotalTickets(e.target.value)}
                min="1"
                placeholder="100"
                required
              />
            </div>
            {error && <div className="text-red-400 text-sm text-left">{error}</div>}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2 rounded font-semibold hover:scale-105 hover:shadow-lg transition"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Đang tạo sự kiện...' : 'Tạo sự kiện'}
            </button>
          </form>
          {contractAddress && (
            <div className="mt-6 p-4 bg-green-100/10 border border-green-400 rounded-xl">
              <div className="font-semibold text-green-300">Địa chỉ contract sự kiện:</div>
              <div className="break-all text-green-400 text-lg font-mono">{contractAddress}</div>
            </div>
          )}
        </div>
      </section>

      {/* Danh sách sự kiện */}
      <section className="w-full py-12 px-4">
        <EventList />
      </section>
    </main>
  );
};

export default CreateEventPage; 